/**
 * A collapsible group of related fields.
 *
 * FireCMS renders a collection's properties as one flat list, which is fine
 * until the list is forty fields long and an editor has to scroll past the
 * Open Graph description to reach the author. There is no grouping primitive,
 * so this builds one out of the two that exist: a property may hide itself from
 * the form (`disabled.hidden`), and any property may be rendered anywhere with
 * `PropertyFieldBinding`.
 *
 * So the members of a group hide themselves from the flat list, and this panel,
 * attached to one anchor property, renders them itself. Nothing about the data
 * changes: the fields are the same fields, validated by the same schema, and
 * `groupedInto` in `collections/guides.tsx` is the only place that has to know.
 */

import React from "react";
import { FieldProps, Property, PropertyFieldBinding } from "@firecms/core";
import { ExpandablePanel, Typography } from "@firecms/ui";

export interface PropertyGroupProps {
    title: string;
    description?: string;
    initiallyExpanded?: boolean;
    /** Sibling property keys to render inside, in order. Excludes the anchor. */
    members: string[];
    /** How the anchor property renders inside the panel. Defaults to its type. */
    selfField?: React.ComponentType<FieldProps<any, any>>;
    selfProps?: unknown;
}

/**
 * The property as it would be without the flag that keeps it out of the flat
 * list. `disabled` is not only a visibility switch in FireCMS, it also disables
 * the input, so leaving it on would render every grouped field read-only.
 */
function shown(property: Property): Property {
    const { disabled, ...rest } = property as Property & { disabled?: unknown };
    return rest as Property;
}

export function PropertyGroupField({
    propertyKey,
    property,
    context,
    customProps
}: FieldProps<any, PropertyGroupProps>) {

    const properties = (context.collection?.properties ?? {}) as Record<string, Property>;

    /* The anchor renders inside its own panel. Its `Field` is this component, so
       it has to be swapped out or the panel would contain itself. */
    const anchor = {
        ...(property as Property),
        Field: customProps.selfField,
        customProps: customProps.selfProps
    } as Property;

    return (
        <ExpandablePanel
            asField
            initiallyExpanded={customProps.initiallyExpanded ?? false}
            title={
                <div className={"flex flex-col"}>
                    <Typography variant={"subtitle2"}>{customProps.title}</Typography>
                    {customProps.description && (
                        <Typography variant={"caption"} className={"opacity-70"}>
                            {customProps.description}
                        </Typography>
                    )}
                </div>
            }>
            <div className={"flex flex-col gap-4 py-2"}>
                <PropertyFieldBinding<any, any> propertyKey={propertyKey}
                                      property={anchor}
                                      context={context}/>
                {customProps.members.map((key) => {
                    const member = properties[key];
                    if (!member) return null;
                    return (
                        <PropertyFieldBinding<any, any> key={key}
                                              propertyKey={key}
                                              property={shown(member)}
                                              context={context}/>
                    );
                })}
            </div>
        </ExpandablePanel>
    );
}
